import { Panel, Skeleton } from '@/components/ui';
import { cx } from '@/lib/cx';
import type { AccountsStats } from '@/lib/types';
import { TileFailureStrip } from '@/components/dashboards/TargetTiles';

/**
 * The card label and figure, in `StatTile`'s anatomy (UI rework, 2026-10).
 *
 * ⚠️ **`StatTile` ITSELF IS NOT NESTED HERE, AND THAT IS DELIBERATE.** It emits
 * `<div>`s and draws its own bordered, rounded box. Each card here is a
 * `<button>` (phrasing content only) inside a `gap-px` hairline grid whose 1px
 * gaps ARE the rules — so a `StatTile` inside would be invalid markup and would
 * draw a second frame inside the grid's. The anatomy is copied instead: label
 * sentence case, muted `text-xs`; figure `text-2xl` semibold `tabular-nums`.
 * ⚠️ *SUPERSEDED: `TILE_FIELD_LABEL` (uppercase, tracked) imported from
 * `TargetTiles` and a `text-3xl` figure.*
 */
const CARD_LABEL = 'text-xs text-fg-muted';

/**
 * THE ACCOUNTS DASHBOARD'S STAT CARDS — counts, each a CONTROL that asks the
 * page to open the matching section already filtered.
 *
 * 🔴 **THE ROW IS PER SECTION SINCE 2026-09-03 — owner's instruction.**
 * *SUPERSEDED: one shared row of four cards standing above all three sections.*
 * Quotes shows three, Invoices four, Future Billables two, and switching section
 * swaps the row. The shared row could only ever describe one section at a time
 * while claiming to describe the page.
 *
 * ⛔ **COUNTS ONLY. NO MONEY AND NO CURRENCY, ON ANY CARD, EVER.** These
 * populations span USD, GBP and EUR *and* rows carrying no currency at all
 * (`d_invoices.currency` is nullable), so one summed figure over any of them is
 * arithmetic across currencies — wrong by construction, and wrong in the
 * confident direction: a total renders perfectly and nothing anywhere reports
 * that it added dollars to pounds. **Do not add a money figure here even if the
 * stats response grows a field that offers one.** The same ruling
 * `/revenue-tracker` makes about its own columns, and the same one the kanban
 * boards make about `totalAmount`.
 *
 * ⚠️ **These are buttons, not read-outs, and the difference decides the markup.**
 * A card that changes what the list below shows is a toggle: it carries
 * `aria-pressed`, it is keyboard reachable, and it looks pressed when it is. It
 * is deliberately NOT a tab — a tablist implies the panels below share one
 * tabstop and are swapped by arrow keys, and these do not.
 *
 * ⚠️ **The visual idiom is `TargetTileGrid`'s, on the owner's instruction that
 * these look like the partnership tiles**: one hairline grid (`gap-px` over
 * `bg-border`, cells painted `bg-surface`, so the 1px gaps read as rules), the
 * same `TILE_FIELD_LABEL` and the same `text-3xl` figure. `TILE_FIELD_LABEL` and
 * `TileFailureStrip` are IMPORTED rather than restated so the two dashboards
 * cannot drift on a label style or on how a failure is worded.
 *
 * ⚠️ **What is NOT shared is the ELEMENT.** `TargetTiles`' `NoFigure` and
 * `TileFigures` emit `<p>`, which is correct inside its `<li>` and INVALID
 * inside a `<button>` — a button's content model is phrasing content. Everything
 * below the label here is therefore a `<span>` with a display utility. React
 * builds the tree through the DOM rather than the HTML parser, so a `<p>` in
 * here would not visibly break; it would simply be wrong, in the way nothing in
 * this repo can see.
 *
 * ⛔ **NO HELPER TEXT.** A label and a number stand alone. The only sentences in
 * this file are the failure strip (the server's own words for why the figures
 * are missing) and the one-word note that separates "we could not find out"
 * from a real zero — both of them name a state the reader cannot otherwise see,
 * which is the documented exception, not a carve-out.
 */

/**
 * The three sections a card can belong to.
 *
 * ⚠️ **`AccountsDashboardPage`'s own `Section` IS THIS UNION — it is declared
 * there as an alias of this type rather than as a second list of three
 * strings.** The alternative was two spellings that agree the day they are
 * written; and the thing that would break when they stopped agreeing is
 * `CARDS`' exhaustive `section` field below, which is only exhaustive over ONE
 * union. It lives here rather than on the page because the page imports this
 * component and not the other way round.
 */
export type AccountsStatSection = 'quotes' | 'invoices' | 'future';

/**
 * Which card is being talked about.
 *
 * ⚠️ **These are NOT the keys on `stats`** — `quotesPending` reads
 * `quotesPendingSignature`, and so on. That is why `CARDS` below carries an
 * explicit `read` function rather than indexing the object by the key: an
 * indexed lookup would look correct and would be reaching for a field that does
 * not exist, which reads back `undefined` and renders as an empty card
 * indistinguishable from a count of nothing.
 *
 * ⚠️ **`quotesPending` KEEPS ITS OLD SPELLING even though two quote cards now
 * sit beside it.** Renaming it to `quotesAwaitingSignature` for symmetry would
 * be a rename of a key that appears in `STAT_CARD_FILTER` on the page and in
 * nothing a user can see — pure churn against a `tsc` error that would find it
 * anyway. The symmetry it buys is worth less than a diff nobody has to read.
 */
export type AccountsStatKey =
  | 'quotesDraft'
  | 'quotesPending'
  | 'quotesAcceptedUnbilled'
  | 'invoicesPending'
  | 'companiesOverdue'
  | 'invoicesOverdue90Plus'
  | 'invoicesDueIn30Days'
  | 'futureBillables'
  | 'futureBillablesNext30Days';

/**
 * ⚠️ **The counts are `AccountsStats` FROM `@/lib/types` — the wire type — and
 * this file deliberately declares no second name for that shape.**
 * `use-accounts-stats.ts` already hands the page an `AccountsStats | null`, so
 * binding to it means the page passes what it holds with no adapter and nothing
 * to keep in step.
 *
 * A narrower structural copy was considered — `overdue-label.ts` takes exactly
 * that route one file over, and documents why — and rejected here for the
 * reason that module's own header gives against itself: a narrower type keeps
 * ACCEPTING the wider one, so a field that changes meaning on the wire fails
 * nothing. That is a real trade for a pure formatter with no other imports; this
 * is a component that already imports from three places, so it buys nothing and
 * costs the guarantee.
 */

/**
 * Every card, in the order it is drawn, grouped by the section it belongs to.
 *
 * 🔴 **THE `section` FIELD IS DECLARED HERE AND NOWHERE ELSE.** The page's
 * `STAT_CARD_FILTER` says which FILTER a card sets and deliberately does not
 * repeat which section it opens — two declarations of one fact drift, and what
 * would drift is a card that lights on one section and navigates to another.
 * `accountsStatSection()` below is how the page asks.
 *
 * ⚠️ **AN EXHAUSTIVE `Record`, NOT AN ARRAY, AND THAT IS THE GUARD.** A member
 * added to `AccountsStatKey` with no entry here is a `tsc` error naming the key;
 * an array would simply have one fewer element and render one fewer card, which
 * looks exactly like a card somebody deliberately removed. The page's
 * `STAT_CARD_FILTER` is the same shape for the same reason, one step further on:
 * a card with no click target refuses to compile there.
 *
 * ⚠️ **The row order is this object's KEY ORDER**, read back with
 * `Object.keys`. That is a real dependency on insertion order — legal for
 * string keys, and the reason none of these keys is numeric-looking.
 *
 * `warnWhenPositive` marks a card where a count above zero is BAD NEWS. Only the
 * two overdue cards carry it: "Due in 30 days" is money arriving and "Planned"
 * is a healthy pipeline, and painting either as a problem would teach a reader
 * to ignore the mark on the two cards that mean it.
 */
const CARDS: Readonly<
  Record<
    AccountsStatKey,
    {
      section: AccountsStatSection;
      label: string;
      /** ⚠️ `number | null` — see `AccountsStats.quotesPendingSignature`. */
      read: (stats: AccountsStats) => number | null;
      warnWhenPositive?: true;
    }
  >
> = {
  /* ---- Quotes: what has not gone out, what is out, what came back ---- */
  quotesDraft: {
    section: 'quotes',
    label: 'Draft quotes',
    read: (stats) => stats.quotesDraft,
  },
  quotesPending: {
    section: 'quotes',
    label: 'Quotes awaiting signature',
    read: (stats) => stats.quotesPendingSignature,
  },
  /*
   * ⚠️ **"not yet invoiced", never "not yet billed"** — a STAGED quote can be
   * billed a line at a time, so "billed" would claim something this count does
   * not measure. The population is quotes a customer has accepted with no
   * invoice raised against them, which is work sold and not yet asked for.
   */
  quotesAcceptedUnbilled: {
    section: 'quotes',
    label: 'Accepted, not yet invoiced',
    read: (stats) => stats.quotesAcceptedUnbilled,
  },

  /* ---- Invoices: what is owed, who has stopped paying, what is landing ---- */
  /*
   * ⚠️ **"pending payment" IS `unsettled`, WHICH INCLUDES DRAFTS.** The label is
   * the owner's word and is kept; the filter panel's own control for the same key
   * is labelled "Unsettled" precisely because that is the more honest word for a
   * control somebody sets by hand. Do not relabel this one "outstanding" or
   * "owed" — both read as *sent and unpaid*, which is narrower than what it counts.
   */
  invoicesPending: {
    section: 'invoices',
    label: 'Invoices pending payment',
    read: (stats) => stats.invoicesPending,
  },
  /*
   * ⚠️ **COMPANIES, NOT INVOICES, AND THE LABEL HAS TO SAY SO** — two invoices
   * overdue for one school count once. It sits between two invoice counts, so
   * the noun is the only thing separating it from them.
   */
  companiesOverdue: {
    section: 'invoices',
    label: 'Companies overdue',
    read: (stats) => stats.companiesOverdue,
    warnWhenPositive: true,
  },
  invoicesOverdue90Plus: {
    section: 'invoices',
    label: 'Overdue 90+ days',
    read: (stats) => stats.invoicesOverdue90Plus,
    warnWhenPositive: true,
  },
  invoicesDueIn30Days: {
    section: 'invoices',
    label: 'Due in 30 days',
    read: (stats) => stats.invoicesDueIn30Days,
  },

  /* ---- Future Billables: everything planned, and what lands soon ---- */
  /*
   * ⚠️ **"Planned" IS THE SECTION UNFILTERED**, which is what makes it the "all"
   * card beside its neighbour rather than a second narrowing. It carries no
   * filter of its own on the page, and clicking it CLEARS the other one.
   */
  futureBillables: {
    section: 'future',
    label: 'Planned',
    read: (stats) => stats.futureBillables,
  },
  futureBillablesNext30Days: {
    section: 'future',
    label: 'Due in the next 30 days',
    read: (stats) => stats.futureBillablesNext30Days,
  },
};

/** Declaration order, which is row order. See `CARDS`. */
const CARD_KEYS = Object.keys(CARDS) as AccountsStatKey[];

/**
 * Which section a card belongs to — the page's single source for it.
 *
 * Exported as a function rather than as the record so there is no second object
 * to keep in step and no way to read a card's section without going through the
 * one declaration.
 */
export function accountsStatSection(key: AccountsStatKey): AccountsStatSection {
  return CARDS[key].section;
}

/**
 * A card's user-visible label — the one place it is spelled.
 *
 * Added 2026-09-14 for the filter summary bar, which names the pressed card
 * above the table. ⚠️ **The bar takes the label rather than paraphrasing it**:
 * these words have been argued over individually (*"Accepted, not yet
 * invoiced"*, never *"not yet billed"*; *"Companies overdue"*, because two
 * invoices for one school count once), so a second wording would quietly make a
 * different claim about the population.
 */
export function accountsStatLabel(key: AccountsStatKey): string {
  return CARDS[key].label;
}

/**
 * Whether a count above zero on this card is BAD NEWS.
 *
 * ⚠️ **It answers a property of the CARD, not of today's figure.** The card
 * tones its own number only when the count is actually positive — at zero an
 * overdue card is ordinary good news and reads like its neighbours — but the
 * summary bar is drawn because somebody PRESSED the card, whatever it reads, so
 * the bar asks the card's standing meaning. ⛔ **Do not "fix" this to consult
 * the count**: it would put a neutral bar under a danger-toned card the moment
 * the figure fell to zero, which is the one moment the two would visibly
 * disagree.
 */
export function accountsStatWarns(key: AccountsStatKey): boolean {
  return CARDS[key].warnWhenPositive === true;
}

/** Every card in a section, in row order. */
export function accountsStatKeysFor(section: AccountsStatSection): readonly AccountsStatKey[] {
  return CARD_KEYS.filter((key) => CARDS[key].section === section);
}

/**
 * The column counts each row is drawn at.
 *
 * 🔴 **EVERY COLUMN COUNT HERE DIVIDES ITS ROW'S CARD COUNT EXACTLY, AND THAT IS
 * A HARD RULE RATHER THAN A PREFERENCE.** The grid is `gap-px` over `bg-border`
 * with each cell painted `bg-surface`, so the 1px gaps are what draw the
 * hairlines between cards — which means an EMPTY CELL is not whitespace, it is a
 * **visible break in the rules**, a gap in the frame with nothing in it. Three
 * cards at `lg:grid-cols-4` would leave exactly that, and it reads as a card
 * that failed to render rather than as a row of three.
 *
 * So the quotes row goes 1 → 3 and never through 2, and the future row goes
 * 1 → 2 and never through 3 or 4. **Whenever a card is added or removed, re-cut
 * that row's counts against the new total; do not add a breakpoint because its
 * neighbour has one.**
 *
 * ⚠️ **WHOLE CLASS LITERALS, NEVER COMPOSED.** Tailwind scans source for
 * complete class names, so a count built as `` `grid-cols-${n}` `` emits no CSS
 * at all — the grid silently falls back to one column at every width, which
 * looks like a deliberate stacked layout rather than like missing styles.
 */
const SECTION_GRID: Readonly<Record<AccountsStatSection, string>> = {
  quotes: 'grid-cols-1 sm:grid-cols-3',
  invoices: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  future: 'grid-cols-1 sm:grid-cols-2',
};

/** The headline figure, shared by every branch so four cards cannot drift. */
const FIGURE = 'block text-2xl font-semibold tabular-nums';

/**
 * What an UNSELECTED card's content does once one of its siblings is pressed —
 * owner's decision, 2026-09-14, taken against the recommendation not to.
 *
 * 🔴 **IT IS ON THE CONTENT, NEVER ON THE CELL, AND THAT IS NOT A STYLE
 * PREFERENCE.** The row is `gap-px` over `bg-border` with each cell painted
 * `bg-surface`, so the 1px gaps ARE the hairlines. Fading the cell fades its
 * background too, the border colour shows through, and the whole card turns grey
 * — which reads as a disabled control rather than as a receded one. Fading only
 * the label and the figure leaves the frame intact.
 *
 * ⚠️ **THEY LIFT BACK ON HOVER AND ON KEYBOARD FOCUS**, which is the guard
 * against the risk the owner accepted: a faded card still has to read as
 * something you can press, and every one of these stays a live button in every
 * state. `group-focus-visible` matters more than the hover — a keyboard reader
 * tabbing along the row would otherwise move through four cards that all look
 * switched off.
 *
 * ⚠️ **`opacity-60`, not lower.** These are figures somebody compares (7 overdue
 * against 41 pending), so the unselected ones have to stay READABLE; the point is
 * that the pressed one dominates, not that the others are hidden.
 */
const DIMMED =
  'opacity-60 transition-opacity duration-150 ease-out group-hover:opacity-100 ' +
  'group-focus-visible:opacity-100 motion-reduce:transition-none';

/**
 * The mark on the selected card.
 *
 * ⚠️ **A tick, and it is the THIRD signal rather than the first.** Selection is
 * already carried by `aria-pressed` (what assistive tech reads) and by the accent
 * rule along the top (a shape, present or absent). The tick is what makes it
 * unmissable at a glance, which is the whole of the owner's instruction.
 *
 * `aria-hidden` deliberately: `aria-pressed` already says pressed, and a second
 * announcement would say it twice.
 */
function SelectedMark() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3 w-3 shrink-0 text-fg"
    >
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}

/**
 * The alert mark on the overdue card, and it is here so COLOUR IS NOT THE ONLY
 * SIGNAL.
 *
 * ⚠️ A red figure and a black one are the same figure to a reader who cannot
 * separate the two hues, so the mark's PRESENCE — a shape that is there or is
 * not — is what actually carries "some of these are overdue". The colour is the
 * second signal, not the first.
 *
 * `aria-hidden` deliberately: a screen reader already hears "3, Companies
 * overdue", which is the whole fact. Announcing a warning as well would say it
 * twice.
 */
function OverdueMark() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3 w-3 shrink-0 text-danger"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/**
 * What one card shows where its number goes.
 *
 * 🔴 **`stats === null` MUST NOT RENDER AS A ROW OF ZEROS.** In this system a zero
 * and a read that never came back look identical on screen, and only one of them
 * is an answer: "nothing is overdue" is good news somebody acts on, and "we could
 * not find out" is not news at all. So a missing figure is a DASH — never a `0`,
 * never a blank — exactly as `TargetTiles`' `NoFigure` does it one file over.
 *
 * ⚠️ **The note is suppressed while the failure strip is showing.** A fact about
 * the whole row is worded once, by the strip; repeated per card it prints the
 * same sentence two to four times across one row, which is what makes a
 * dashboard with nothing on it read as broken rather than as empty.
 *
 * ⚠️ **A non-finite count falls to the dash rather than rendering `NaN`.** The
 * guard is not decoration: `Number.isFinite` is the only thing between a server
 * that omitted a key and the word "NaN" sitting where a figure belongs.
 */
function CardFigure({
  loading,
  value,
  note,
  warn,
}: {
  loading: boolean;
  value: number | null;
  note: string | undefined;
  warn: boolean;
}) {
  if (loading) return <Skeleton className="h-8 w-16" />;

  if (value === null || !Number.isFinite(value)) {
    return (
      <>
        <span className={cx(FIGURE, 'text-fg-subtle')}>—</span>
        {note !== undefined && <span className="mt-0.5 block text-xs text-fg-subtle">{note}</span>}
      </>
    );
  }

  return (
    <span className={cx(FIGURE, warn ? 'text-danger' : 'text-fg')}>{value.toLocaleString()}</span>
  );
}

export function AccountsStatCards({
  section,
  sectionLabel,
  stats,
  loading,
  error,
  active,
  onSelect,
}: {
  /** Which row to draw. The cards follow the section — see `CARDS`. */
  section: AccountsStatSection;
  /**
   * The section's user-visible name, for the list's own label.
   *
   * ⚠️ **PASSED IN RATHER THAN SPELLED HERE.** `SECTION_LABEL` on the page is
   * the one place a section's name is written — the chip, the panel heading and
   * the Sheets frame title all read it — and a fourth spelling in this file
   * would be the one that disagrees after the next rename. (It has already been
   * renamed twice: "Future Billable", then the plural.) The call site derives
   * both this and `section` from one value, so they cannot arrive mismatched.
   */
  sectionLabel: string;
  stats: AccountsStats | null;
  loading: boolean;
  error: string | null;
  /** Which card is currently reflected by the list below, if any. */
  active: AccountsStatKey | null;
  onSelect: (key: AccountsStatKey) => void;
}): React.JSX.Element {
  const keys = accountsStatKeysFor(section);

  return (
    <Panel className="overflow-hidden">
      {/*
       * ⚠️ Shown whenever `error` is set, INDEPENDENTLY of whether figures
       * arrived. A refresh that failed over figures already on screen is a real
       * state: the numbers are stale rather than absent, and the honest render
       * is both — the stale figures and the reason they are stale. Swapping the
       * figures for dashes as well would throw away a row of
       * true-as-of-a-moment-ago answers to say one thing the strip already says.
       */}
      {error !== null && (
        <TileFailureStrip headline="Couldn’t read these figures." message={error} />
      )}

      {/*
       * ⚠️ **THE LABEL NAMES THE SECTION, because the row CHANGES with it.** A
       * screen-reader user moving between sections gets a different list of a
       * different length under the same heading otherwise, with nothing saying
       * which one they are in — and unlike a sighted reader they cannot see the
       * chip above it that answers the question.
       */}
      <ul
        aria-label={`${sectionLabel} summary`}
        className={cx('m-0 grid list-none gap-px bg-border p-0', SECTION_GRID[section])}
      >
        {keys.map((key) => {
          const card = CARDS[key];
          const selected = active === key;
          const value = stats === null ? null : card.read(stats);
          /*
           * The warning tone is a property of the COUNT, not of the card: at
           * zero an overdue card is ordinary good news and must read like its
           * neighbours.
           */
          const warn =
            card.warnWhenPositive === true && value !== null && Number.isFinite(value) && value > 0;
          /*
           * ⚠️ **`active !== null`, NOT `!selected`** — with nothing pressed,
           * every card is at full strength. Dimming is a comparison against a
           * chosen sibling, so with no sibling chosen there is nothing to recede
           * from, and a row that arrived faded would read as a dashboard that had
           * not finished loading.
           */
          const dimmed = active !== null && !selected;

          return (
            <li key={key} className="flex min-w-0">
              {/*
               * ⚠️ **The card stays a live button in EVERY state, including
               * while loading and after a failed read.** Not knowing the count
               * is not a reason to withhold the only way into the section — the
               * house rule is that a control wrongly disabled is a false
               * statement nobody is watching, where one wrongly enabled costs a
               * click. An affordance that comes and goes also reads as a broken
               * app, which is exactly what swapping the tag per state would do.
               *
               * ⚠️ **`-outline-offset-2` pulls the focus ring INSIDE the cell.**
               * The default `outline-offset: 1px` from `index.css` would be
               * clipped by this `Panel`'s `overflow-hidden` on the corner cards,
               * so the keyboard user at the edge of the row would lose the ring
               * entirely — silently, and only at the positions on the edge.
               */}
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => onSelect(key)}
                className={cx(
                  /*
                   * ⚠️ **`group` is what lets a dimmed sibling lift on hover and
                   * on focus.** See `DIMMED` — without it the fade would have to
                   * be re-stated per child with its own hover, and the figure and
                   * the label would lift at different moments.
                   */
                  'group relative flex min-w-0 flex-1 flex-col px-4 py-4 text-left',
                  'transition-colors duration-150 ease-out motion-reduce:transition-none',
                  'focus-visible:-outline-offset-2',
                  selected
                    ? 'bg-surface-active'
                    : 'bg-surface hover:bg-surface-hover active:bg-surface-active',
                )}
              >
                {/*
                 * ⚠️ **The selected card is marked by a BAR AS WELL AS a
                 * background step**, so selection survives for a reader who
                 * cannot separate the two surface tones — a mark that is present
                 * or absent is a shape difference, not a hue one. `aria-pressed`
                 * is the third signal and the one assistive tech actually reads.
                 *
                 * ⚠️ Absolutely positioned rather than a border, so selecting a
                 * card does not resize it and shunt the row. `accent-brand` is
                 * rationed to primary actions and active state — this is the
                 * second of those, which is the whole licence for it here.
                 */}
                {/*
                 * ⚠️ **THICKENED TO `h-1` ON 2026-09-14**, with the tick beside
                 * the label, on the owner's instruction that a pressed card read
                 * unmistakably. A 2px hairline was doing this work alone and was
                 * the whole reason the filtering read as not having happened.
                 *
                 * ⛔ **THE RULE STAYS `accent-brand` EVEN ON THE TWO OVERDUE
                 * CARDS.** `accent-brand` is rationed to primary actions and
                 * ACTIVE STATE, and this is active state; the danger tone on this
                 * row means *these figures are bad news*, which is a fact about
                 * the count and not about which card is pressed. The summary bar
                 * below is where the card's own tone is carried through — see
                 * `accountsStatWarns`.
                 */}
                {/*
                 * ⚠️ *SUPERSEDED (UI rework, 2026-10): the `accent-brand` bar
                 * described in the two notes above is REMOVED.* A selected
                 * toggle now takes the neutral selected style — the
                 * `bg-surface-active` step, the tick (a shape, so colour is not
                 * the only signal) and `aria-pressed`. Three visual signals for
                 * one state read as decoration; the accent is rationed to
                 * primary actions, the active nav row and focus.
                 */}

                <span
                  className={cx(
                    'flex items-center gap-1.5',
                    CARD_LABEL,
                    selected && 'text-fg',
                    dimmed && DIMMED,
                  )}
                >
                  {warn && <OverdueMark />}
                  {selected && <SelectedMark />}
                  <span className="min-w-0">{card.label}</span>
                </span>

                {/*
                 * `mt-auto` bottom-aligns the figures across a row, so a label
                 * that wraps at one width cannot leave one number sitting a line
                 * below its neighbours.
                 */}
                <span className={cx('mt-auto block pt-2', dimmed && DIMMED)}>
                  <CardFigure
                    loading={loading}
                    value={value}
                    note={error === null ? 'Not available' : undefined}
                    warn={warn}
                  />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
